import { type PersistOptions, persist, type StorageValue } from 'zustand/middleware';
import type { StateCreator, StoreMutatorIdentifier } from 'zustand/vanilla';

import type { StoreInterface } from './index.ts';

type DeferredPersistOptions<S extends StoreInterface> = PersistOptions<S> & {
	hydrateOnResolve?: Promise<void>;
	debounceMs?: number;
	persistToIndexedDb?: (state: S) => Promise<void>;
	preparePersistState?: (state: S) => Partial<S>;
};

interface IdleDeadlineLike {
	readonly didTimeout: boolean;
	timeRemaining: () => number;
}

type IdleWindow = Window & {
	requestIdleCallback?: (callback: (deadline: IdleDeadlineLike) => void, options?: { timeout: number }) => number;
	cancelIdleCallback?: (handle: number) => void;
};

function isRecord(value: unknown): value is Record<string, unknown> {
	return typeof value === 'object' && value !== null;
}

function toSerializableValue(value: unknown): unknown {
	if (value instanceof Map) {
		return Array.from(value.entries(), ([key, mapValue]) => [key, toSerializableValue(mapValue)]);
	}

	if (Array.isArray(value)) {
		return value.map((arrayValue) => toSerializableValue(arrayValue));
	}

	if (isRecord(value)) {
		const serializable: Record<string, unknown> = {};
		for (const [key, recordValue] of Object.entries(value)) {
			if (typeof recordValue === 'function' || recordValue === undefined) {
				continue;
			}
			serializable[key] = toSerializableValue(recordValue);
		}
		return serializable;
	}

	return value;
}

function toMap<TKey, TValue>(value: unknown): Map<TKey, TValue> {
	if (value instanceof Map) {
		return value;
	}

	if (Array.isArray(value)) {
		return new Map(value as [TKey, TValue][]);
	}

	if (isRecord(value)) {
		return new Map(Object.entries(value) as [TKey, TValue][]);
	}

	return new Map<TKey, TValue>();
}

/**
 * Used for syncing our store with localStorage. This is a modified version of the persist middleware to handle Maps.
 */
export default function deferredPersist<
	S extends StoreInterface,
	CustomSetState extends [StoreMutatorIdentifier, unknown][] = [StoreMutatorIdentifier, unknown][],
	CustomGetState extends [StoreMutatorIdentifier, unknown][] = [StoreMutatorIdentifier, unknown][],
>(
	config: StateCreator<S, [...CustomSetState, ['zustand/persist', unknown]], CustomGetState, S>,
	deferredOptions: DeferredPersistOptions<S>,
) {
	const {
		debounceMs: debounceMsOption,
		hydrateOnResolve: hydrateOnResolveOption,
		persistToIndexedDb,
		preparePersistState,
		...persistOptions
	} = deferredOptions;
	const hydrateOnResolve = hydrateOnResolveOption || Promise.resolve();
	const debounceMs = debounceMsOption ?? 10_000;
	const getStorage = () => deferredOptions.storage || localStorage;
	const pendingWrites = new Map<string, StorageValue<S>>();
	const pendingTimeouts = new Map<string, ReturnType<typeof setTimeout>>();
	const pendingIdleCallbacks = new Map<string, number>();

	const cancelScheduledWrite = (name: string) => {
		const timeout = pendingTimeouts.get(name);
		if (timeout) {
			clearTimeout(timeout);
			pendingTimeouts.delete(name);
		}

		if (typeof window === 'undefined') {
			return;
		}

		const idleWindow = window as IdleWindow;
		const idleCallback = pendingIdleCallbacks.get(name);
		if (idleCallback !== undefined && idleWindow.cancelIdleCallback) {
			idleWindow.cancelIdleCallback(idleCallback);
		}
		pendingIdleCallbacks.delete(name);
	};

	const flushWrite = (name: string) => {
		const pending = pendingWrites.get(name);
		if (!pending) {
			return;
		}

		cancelScheduledWrite(name);

		const preparedState = preparePersistState ? preparePersistState(pending.state as S) : pending.state;
		const serialized = JSON.stringify({
			state: toSerializableValue(preparedState),
		});

		getStorage().setItem(name, serialized as string & StorageValue<S>);

		if (typeof window !== 'undefined' && window.ENV.LOCAL_MODE && persistToIndexedDb) {
			void persistToIndexedDb(pending.state as S);
		}

		pendingWrites.delete(name);
	};

	const options: PersistOptions<S> = {
		...persistOptions,
		storage: {
			getItem: async (name: string) =>
				hydrateOnResolve.then(() => {
					const str = getStorage().getItem(name);
					if (str) {
						const parsed = JSON.parse(str as string) as unknown;
						const parsedState =
							isRecord(parsed) && isRecord(parsed.state) ? parsed.state : isRecord(parsed) ? parsed : {};
						const state = {
							...parsedState,
							instrumentFetches: toMap(parsedState.instrumentFetches),
							instrumentSockets: toMap(parsedState.instrumentSockets),
							instrumentsMerged: toMap(parsedState.instrumentsMerged),
						} as S;
						const version = isRecord(parsed) && typeof parsed.version === 'number' ? parsed.version : undefined;
						return {
							state,
							version,
						};
					} else {
						return {
							state: {
								instrumentFetches: new Map(),
								instrumentSockets: new Map(),
								instrumentsMerged: new Map(),
								tickerList: [],
							} as unknown as S,
						};
					}
				}),
			removeItem: async (name: string) =>
				hydrateOnResolve.then(() => {
					cancelScheduledWrite(name);
					pendingWrites.delete(name);
					getStorage().removeItem(name);
				}),
			setItem: async (name: string, newValue: StorageValue<S>) =>
				hydrateOnResolve.then(() => {
					pendingWrites.set(name, newValue);
					cancelScheduledWrite(name);

					if (typeof window !== 'undefined') {
						const idleWindow = window as IdleWindow;
						if (idleWindow.requestIdleCallback) {
							const idleCallback = idleWindow.requestIdleCallback(
								() => {
									pendingIdleCallbacks.delete(name);
									flushWrite(name);
								},
								{ timeout: debounceMs },
							);
							pendingIdleCallbacks.set(name, idleCallback);
							return;
						}
					}

					pendingTimeouts.set(
						name,
						setTimeout(() => {
							flushWrite(name);
						}, debounceMs),
					);
				}),
		},
	};

	if (typeof window !== 'undefined') {
		const flushAll = () => {
			for (const name of pendingWrites.keys()) {
				flushWrite(name);
			}
		};

		window.addEventListener('pagehide', flushAll);
		window.addEventListener('visibilitychange', () => {
			if (document.visibilityState === 'hidden') {
				flushAll();
			}
		});
	}

	return persist<S, CustomSetState, CustomGetState>(config, options);
}
